/**
 * The single source of truth for "does this tool have a score we may use".
 *
 * A tool can finish successfully and still decline to grade: crawl-check
 * returns `overallScore: null` with `scoreStatus: 'withheld'` when the scan was
 * too blocked to grade honestly. That null is a REFUSAL, not a zero.
 *
 * Reading it as a number is the bug this module exists to prevent. In
 * JavaScript `null + n` coerces to `0 + n`, and `null < 50` is `true`, so a
 * withheld tool silently landed in averages as a zero AND was counted as a
 * failing tool. The dashboard showed "--" in the tool row while the headline
 * had already scored it 0 — the screen contradicting itself.
 *
 * Every consumer that averages, counts, ranks, persists, exports or narrates
 * tool scores must go through here. `lib/radar/parity.ts` applies the same
 * rule for the projection, so the dashboard and the roadmap agree on which
 * tools count.
 */

import { TOOLS } from '@/types/radar'
import type { ToolKey, ToolState } from '@/types/radar'

export interface ScoredTool {
  key: ToolKey
  score: number
}

export interface ToolScoreAggregate {
  /**
   * Mean over gradeable tools only, or null when nothing was gradeable.
   * NEVER 0 as a stand-in for "no data" — 0 is a real, earned score.
   */
  score: number | null
  /** Tools that produced a usable score. The denominator. */
  scoredCount: number
  /** Tools that finished, gradeable or not. */
  completedCount: number
  /** Finished but declined to grade, in canonical TOOLS order. */
  withheldTools: ToolKey[]
  /** The gradeable tools and their scores, for callers that need detail. */
  scored: ScoredTool[]
}

/** True when raw tool data carries a score that may be consumed or narrated. */
export function hasUsableScoreData(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false
  const scoreData = data as {
    scoreStatus?: unknown
    overallScore?: unknown
  }
  return (
    scoreData.scoreStatus !== 'withheld' &&
    typeof scoreData.overallScore === 'number'
  )
}

/**
 * True when a finished tool produced a score we may use.
 *
 * Deliberately checks the VALUE, not just `scoreStatus`: a tool that predates
 * `scoreStatus` (older saved runs) can still carry a null score, and a future
 * tool may withhold without adopting the field.
 */
export function hasUsableScore(state: ToolState | undefined): boolean {
  if (!state || state.status !== 'done' || !state.data) return false
  return hasUsableScoreData(state.data)
}

/** The score if usable, else null. Never coerces. */
export function usableScore(state: ToolState | undefined): number | null {
  return hasUsableScore(state) ? (state!.data.overallScore as number) : null
}

/**
 * Aggregate across a tools record. Withheld tools are EXCLUDED from the mean
 * and from every count derived from it — they contribute nothing and are not
 * failures.
 */
export function aggregateToolScores(
  tools: Partial<Record<ToolKey, ToolState>>
): ToolScoreAggregate {
  const scored: ScoredTool[] = []
  const withheldTools: ToolKey[] = []
  let completedCount = 0

  for (const [key, state] of Object.entries(tools) as [ToolKey, ToolState][]) {
    if (!state || state.status !== 'done' || !state.data) continue
    completedCount++
    const score = usableScore(state)
    if (score === null) withheldTools.push(key)
    else scored.push({ key, score })
  }

  // Object key order is deterministic but not guaranteed to match TOOLS, and
  // this list is rendered to users. Sort it into the canonical order the
  // contract above promises.
  const toolOrder = TOOLS.map(t => t.key)
  withheldTools.sort((a, b) => toolOrder.indexOf(a) - toolOrder.indexOf(b))

  return {
    // Guarded denominator: everything withheld means "not enough data to
    // score", which callers must render as such — not as 0, not as NaN.
    score:
      scored.length > 0
        ? Math.round(
            scored.reduce((sum, s) => sum + s.score, 0) / scored.length
          )
        : null,
    scoredCount: scored.length,
    completedCount,
    withheldTools,
    scored,
  }
}

/** A tool at or above this score counts as passing in the "Pass rate" stat. */
export const PASS_THRESHOLD = 70

/**
 * Tools that scored at or above `PASS_THRESHOLD`.
 *
 * The dashboard stat strip and the marketing demo have to agree, so the rule
 * lives here instead of being re-expressed at each call site. It was expressed
 * twice once, and the two immediately disagreed: the demo used `>= 50` and
 * reported 7/13 where the product reported 6/13 for the same tool scores.
 *
 * Withheld and failed tools are excluded by `hasUsableScore`, never counted as
 * a failure — the denominator is `scoredCount`, not the tool count.
 */
export function passingCount(
  tools: Partial<Record<ToolKey, ToolState>>
): number {
  return Object.values(tools).filter(
    s => hasUsableScore(s) && (s.data.overallScore as number) >= PASS_THRESHOLD
  ).length
}
